// Every word on the Warike site but the legal text and the posts, taken from warike.tech as it reads
// today, in its one language. `{product}` never appears here: that place reads the other product's
// name (ADR-035 point 3).

export const WARIKE_SITE = {
  url: "https://warike.tech",
  logo: "/logo-square.png",
  status: "https://status.warike.tech",
  booking: "https://cal.com/warike-technologies/discovery",
} as const;

export const WARIKE_WORDS = {
  brand: "Warike",
  /** What a screen reader calls the drawn logo. */
  logoLabel: "Warike Technologies",
  legalTitle: (name: string) => `Warike - ${name}`,
  feedTitle: "Warike Blog",
  twinTitle: "Markdown",
  legalUpdatedIso: "2026-09-25",
  /** What a legal page's head reads, so the first page's bundle does not carry the legal text. */
  legal: {
    "privacy-policy": {
      name: "Privacy Policy",
      description:
        "How Warike handles the limited data this website collects under the GDPR, and the choices you have over it.",
    },
    "terms-conditions": {
      name: "Terms & Conditions",
      description: "The terms that govern your use of the Warike website.",
    },
    impressum: {
      name: "Impressum",
      description:
        "Legal notice and company information for Warike UG (haftungsbeschränkt), as required by § 5 DDG.",
    },
  },
  home: {
    title: "Warike - Autonomous intelligence for the physical world",
    headline: "Warike Technologies",
    description:
      "Warike builds autonomous intelligence that reads the physical world, so companies can act on what it reveals instead of drowning in raw data.",
    /** The landing's subtitle. The description above is the page's head and stays as warike.tech has it (D1343). */
    subtitle:
      "We integrate autonomous intelligence into businesses, with insights that come from the physical world.",
  },
  footer: {
    groups: [
      {
        title: "Company",
        links: [
          { label: "Insights", href: "/blog" },
          { label: "Contact", href: "/contact" },
          { label: "Status", href: WARIKE_SITE.status },
          { label: "Brand", href: "/brand" },
        ],
      },
      {
        title: "Legal",
        links: [
          { label: "Privacy", href: "/legal/privacy-policy" },
          { label: "Terms", href: "/legal/terms-conditions" },
          { label: "Impressum", href: "/legal/impressum" },
        ],
      },
    ],
    legal: (year: number) => `© ${year} Warike. All rights reserved.`,
  },
  notFound: {
    pageTitle: "Warike - Page not found",
    metaDescription:
      "This page does not exist. Start from the Warike homepage, read the latest insights, or search the site.",
    code: "404",
    title: "This page does not exist",
    description: "The link may be old or mistyped. Start from the homepage, or read the latest insights.",
    home: "Go home",
    insights: "Read insights",
  },
  consent: {
    title: "We use cookies",
    before:
      "We use cookies to analyse traffic and improve your experience. Accept all, keep only what’s necessary, or choose which to allow.",
    link: "Privacy policy",
    accept: "Accept all",
    reject: "Only necessary",
    choose: "Customize",
    choices: {
      title: "Cookie preferences",
      description:
        "We use cookies to keep the site working and, with your consent, to understand how it is used.",
      required: {
        title: "Necessary",
        description: "Essential for the site to function. Always on.",
        label: "Always on",
      },
      analytics: {
        title: "Analytics",
        description: "Helps us understand how the site is used. Off unless you allow it.",
      },
      save: "Save preferences",
    },
  },
  contact: {
    title: "Warike - Book a discovery call",
    description:
      "Book a discovery call about edge AI and autonomous systems, generative and agentic AI, or technology for fresh produce, supply chains and agriculture.",
    heading: "Talk to us",
    lede: "Tell us what you're working on. Bring your use case, pick a time, and leave the call with a clear next step.",
    topics: [
      {
        icon: "radar",
        title: "Edge AI and autonomous systems",
        subtitle:
          "Drones, sensors and machines that process data in the field, without the cloud, and act on what they find.",
      },
      {
        icon: "bot",
        title: "Generative AI and agentic transformation",
        subtitle: "Putting language models and agents to work inside your team's everyday processes.",
      },
      {
        icon: "sprout",
        title: "Fresh produce, supply chain and agriculture",
        subtitle: "Technology for growing, moving and selling fresh produce, from the field to the shelf.",
      },
    ],
    notLoading: "Calendar not loading?",
    openOnCal: "Open it on cal.com",
    noScript: "The calendar needs JavaScript.",
    bookOnCal: "Book on cal.com",
  },
  blog: {
    title: "Warike - Insights",
    description:
      "Notes on building Warike: autonomous intelligence that reads the physical world. Engineering and product writing lands here.",
    name: "Insights",
    eyebrow: "Notes from technology",
    lede: "We are building Warike in the open. Writing on autonomous intelligence for the physical world lands here.",
    filter: "Filter by tag",
    tags: "Tags",
    faqTitle: "Frequently asked questions",
    share: "Share",
    shareOnX: "Share on X",
    shareOnLinkedIn: "Share on LinkedIn",
    copyLink: "Copy link",
    copied: "Copied",
    previous: "Previous",
    next: "Next",
    tagTitle: (tag: string) => `Warike - Insights on ${tag}`,
    tagDescription: (tag: string) =>
      `Warike posts tagged ${tag}: notes on building autonomous intelligence that reads the physical world.`,
  },
  /** The page's head. Its own words are in `brand-book-words.ts`, so the first page does not carry them. */
  brandPage: {
    title: "Warike - Brand",
    description:
      "The Warike mark, colours, type and logo files, as SVG and PNG for light and dark backgrounds, with the company's own words to copy.",
  },
} as const;
